import { useState, useEffect } from 'react'
import { Image, Card, CardBody, CardFooter, Tooltip } from '@heroui/react'
import {
  InformationCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline'

import { useToast } from '../../../hooks'
import {
  resolveGifUrl,
  copyToClipboard,
  getPublicGifUrl,
  getPreviewPageUrl,
} from '../../gifs/services'
import { gifDisplayTitle } from '../../gifs/types'
import type { Gif } from '../../gifs/types'
import type { RecommendationResult } from '../types'
import {
  LoadingState,
  ErrorState,
  StateContainer,
} from '../../shared/components'

import { AnalysisCard } from './AnalysisCard'
import { ActionButtons } from './ActionButtons'

export interface RecommendationItemProps {
  readonly recommendation: RecommendationResult['recommendations'][0]
  readonly gif: Gif | null
  readonly isLoading: boolean
  readonly onItemClick?: (gif: Gif) => void
}

export const RecommendationItem = ({
  recommendation,
  gif,
  isLoading,
  onItemClick,
}: RecommendationItemProps) => {
  const [gifUrl, setGifUrl] = useState<string>('')
  const [urlError, setUrlError] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const toast = useToast()

  useEffect(() => {
    if (!gif) return

    let isMounted = true

    const resolveUrl = async () => {
      try {
        const resolvedUrl = await resolveGifUrl(gif.gifUrl)
        if (isMounted) {
          setGifUrl(resolvedUrl)
          setUrlError(false)
        }
      } catch (error) {
        console.error('Error resolving GIF URL:', error)
        if (isMounted) {
          setUrlError(true)
        }
      }
    }

    resolveUrl()

    return () => {
      isMounted = false
    }
  }, [gif])

  const handleCopyUrl = async () => {
    if (!gif) return

    try {
      const url = getPreviewPageUrl(gif.gifUrl)
      const success = await copyToClipboard(url)
      if (success) {
        toast.success('Link copied to clipboard!')
      } else {
        toast.error('Failed to copy link to clipboard')
      }
    } catch (error) {
      console.error('Copy error:', error)
      toast.error('Failed to copy link to clipboard')
    }
  }

  const handleCopyCaption = async (event: React.MouseEvent) => {
    // Never bubble a caption-copy click up to a pressable card wrapper.
    event.stopPropagation()
    if (!recommendation.caption) return

    const success = await copyToClipboard(recommendation.caption)
    if (success) {
      toast.success('Caption copied to clipboard!')
    } else {
      toast.error('Failed to copy caption to clipboard')
    }
  }

  if (isLoading) {
    return <LoadingState />
  }

  if (!gif) {
    return (
      <ErrorState
        title="GIF not found"
        subtitle={`ID: ${recommendation.gifId}`}
      />
    )
  }

  const hasDetails =
    !!recommendation.reasoning ||
    (recommendation.strengths?.length ?? 0) > 0 ||
    (recommendation.limitations?.length ?? 0) > 0

  const renderImage = () => {
    if (urlError) {
      return (
        <StateContainer className="w-80 h-40 flex items-center justify-center bg-default-100 rounded-lg">
          <div className="text-center">
            <p className="text-default-500 text-sm">Failed to load GIF</p>
            <p className="text-default-400 text-xs mt-1">
              {gifDisplayTitle(gif)}
            </p>
          </div>
        </StateContainer>
      )
    }

    const tooltipContent = (
      <div className="p-2 max-w-xs">
        <p className="text-sm font-medium">{gifDisplayTitle(gif)}</p>
        {gif.reference.source && (
          <p className="text-xs text-default-400 mt-1">
            {gif.reference.source}
            {gif.reference.year && ` (${gif.reference.year})`}
          </p>
        )}
      </div>
    )

    return (
      <Tooltip content={tooltipContent} placement="top">
        <div className="w-full h-full flex items-end justify-center">
          <Image
            src={gifUrl === '' ? undefined : gifUrl}
            alt={gifDisplayTitle(gif)}
            className="max-w-full max-h-50 object-contain rounded-lg"
            classNames={{
              wrapper: 'flex items-center justify-center',
              img: 'max-w-full max-h-50 object-contain rounded-lg',
            }}
          />
        </div>
      </Tooltip>
    )
  }

  const handleCardClick = () => {
    if (onItemClick && gif) {
      onItemClick(gif)
    }
  }

  return (
    <Card
      className={`w-full max-w-md h-full glass-card flex flex-col ${onItemClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
      isPressable={!!onItemClick}
      onPress={handleCardClick}
    >
      <CardBody className="p-3 flex-1 flex flex-col gap-3">
        {/* GIF Image */}
        <div className="h-48 flex">{renderImage()}</div>

        {/* Caption — the sender's own words, styled like the message text it is */}
        {recommendation.caption && (
          <div className="relative rounded-2xl rounded-tl-sm bg-primary/10 pl-3 pr-9 py-2">
            <p className="text-sm text-foreground leading-snug italic">
              “{recommendation.caption}”
            </p>
            <Tooltip content="Copy caption" placement="top">
              <button
                type="button"
                aria-label="Copy caption"
                onClick={handleCopyCaption}
                className="absolute top-1.5 right-1.5 p-1 rounded-md text-primary/70 hover:text-primary hover:bg-primary/10 transition-colors"
              >
                <ClipboardDocumentIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        )}

        {/* Per-gif reasoning/strengths — demoted behind an unobtrusive disclosure */}
        {hasDetails && (
          <div>
            <button
              type="button"
              aria-expanded={showDetails}
              aria-label={showDetails ? 'Hide why' : 'Why this gif?'}
              onClick={(event) => {
                event.stopPropagation()
                setShowDetails((prev) => !prev)
              }}
              className="inline-flex items-center gap-1 text-xs text-default-400 hover:text-default-600 transition-colors"
            >
              <InformationCircleIcon className="w-4 h-4" />
              {showDetails ? 'Hide why' : 'Why this?'}
            </button>
            {showDetails && (
              <div className="mt-2">
                <AnalysisCard recommendation={recommendation} />
              </div>
            )}
          </div>
        )}
      </CardBody>

      <CardFooter className="pt-2 pb-4 px-2 mt-auto">
        <ActionButtons
          gifUrl={gifUrl}
          urlError={urlError}
          onCopyUrl={handleCopyUrl}
          gifId={recommendation.gifId}
          cleanDownloadUrl={gif ? getPublicGifUrl(gif.gifUrl) : undefined}
        />
      </CardFooter>
    </Card>
  )
}
